// Datos estructurados (JSON-LD) de las páginas de servicio, de distrito y de FAQs.
//
// Viven fuera de los componentes (ServicePage, GeoServicePage, FAQAccordion) porque los usa el
// head() de las rutas, que queda en el chunk de entrada: si el head() los importaba desde el
// componente, el componente entero y el contenido de las 6 páginas de servicio se descargaban en
// todas las páginas públicas, también en la home (~55 KB sin comprimir, PageSpeed oct. 2026).

import type { Distrito } from "@/data/geo";
import type { VistaServicio } from "@/lib/page-content/vista";
import { applyServiceOverride, type ServiceSchemaData } from "@/lib/seo-schema-apply";
import { SITE_URL } from "@/lib/site";

export interface FaqJsonLdItem {
  q: string;
  a: string;
}

export function faqJsonLd(items: FaqJsonLdItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function serviceJsonLd(name: string, description: string, slug: string, image?: string) {
  const imageUrl = image ? (image.startsWith("http") ? image : `${SITE_URL}${image}`) : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${slug}#service`,
    name,
    description,
    inLanguage: "es-PE",
    ...(imageUrl ? { image: imageUrl } : {}),
    areaServed: { "@type": "City", name: "Lima" },
    provider: {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#organization`,
      name: "Improve Yourself",
      url: SITE_URL,
    },
    url: `${SITE_URL}${slug}`,
  };
}

export function serviceBreadcrumbJsonLd(name: string, slug: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Servicios", item: `${SITE_URL}/servicios` },
      { "@type": "ListItem", position: 3, name, item: `${SITE_URL}${slug}` },
    ],
  };
}

export function geoServiceJsonLd(serviceName: string, slug: string, distrito: Distrito) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${slug}/${distrito.slug}#service`,
    name: `${serviceName} en ${distrito.name}`,
    description: `${serviceName} en ${distrito.name}, Lima. ${distrito.referencia}`,
    inLanguage: "es-PE",
    areaServed: {
      "@type": "AdministrativeArea",
      name: distrito.name,
      containedInPlace: { "@type": "City", name: "Lima" },
    },
    provider: {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#organization`,
      name: "Improve Yourself",
      url: SITE_URL,
    },
    url: `${SITE_URL}${slug}/${distrito.slug}`,
  };
}

export function geoBreadcrumbJsonLd(serviceName: string, slug: string, distrito: Distrito) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Servicios", item: `${SITE_URL}/servicios` },
      {
        "@type": "ListItem",
        position: 3,
        name: serviceName,
        item: `${SITE_URL}${slug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: `${serviceName} en ${distrito.name}`,
        item: `${SITE_URL}${slug}/${distrito.slug}`,
      },
    ],
  };
}

/**
 * Lo que el head() de una página de servicio necesita de su contenido. Lo arma el loader (que va
 * en el chunk de la ruta, con el contenido) y el head() lo lee de `loaderData`: así el contenido no
 * entra al chunk de entrada. Solo texto plano: se serializa en el HTML.
 */
export interface DatosHeadServicio {
  h1: string;
  intro: string;
  eyebrow: string;
  faqs: FaqJsonLdItem[];
}

export function datosHeadServicio(vista: VistaServicio): DatosHeadServicio {
  return {
    h1: vista.h1,
    intro: vista.intro,
    eyebrow: vista.eyebrow,
    faqs: vista.faqs.map((f) => ({ q: f.q, a: f.a.texto })),
  };
}

/** Datos estructurados de una página de servicio a partir de `datosHeadServicio`. */
export function jsonLdServicio(
  datos: DatosHeadServicio,
  slug: string,
  image: string | undefined,
  schemaOverride: Partial<ServiceSchemaData> | null,
): { type: string; children: string }[] {
  return [
    {
      type: "application/ld+json",
      children: JSON.stringify(
        applyServiceOverride(serviceJsonLd(datos.h1, datos.intro, slug, image), schemaOverride),
      ),
    },
    { type: "application/ld+json", children: JSON.stringify(faqJsonLd(datos.faqs)) },
    {
      type: "application/ld+json",
      children: JSON.stringify(serviceBreadcrumbJsonLd(datos.eyebrow, slug)),
    },
  ];
}
